import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/lib/admin-constants";
import {
  Upload, Plus, Search, Trash2, Loader2, Building2, Mail, Phone,
  Globe, MapPin, Flame, Snowflake, Thermometer, ChevronDown,
  ChevronRight, X, Copy, Send, FileText, Clock, Tag, Settings,
  Pencil, ArrowUpRight, ArrowDownLeft, RotateCcw,
  Check, Eye, MousePointerClick, AlertTriangle,
  LayoutGrid, List, ArrowUpDown, ArrowUp, ArrowDown,
} from "lucide-react";

const WARMTH_CONFIG = {
  cold: { icon: Snowflake, label: "Cold", color: "text-blue-500", bg: "bg-blue-50 border-blue-200" },
  warm: { icon: Thermometer, label: "Warm", color: "text-amber-500", bg: "bg-amber-50 border-amber-200" },
  hot: { icon: Flame, label: "Hot", color: "text-red-500", bg: "bg-red-50 border-red-200" },
};

const formatTime = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const SponsorPipeline = () => {
  const [sponsors, setSponsors] = useState([]);
  const [stages, setStages] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [warmthFilter, setWarmthFilter] = useState("all");
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [importing, setImporting] = useState(false);
  const [draggedSponsor, setDraggedSponsor] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [collapsedStages, setCollapsedStages] = useState({});
  const [orgSettings, setOrgSettings] = useState([]);
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkTemplateId, setBulkTemplateId] = useState("");
  const [bulkAttachDeck, setBulkAttachDeck] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [unreadSponsors, setUnreadSponsors] = useState({});
  const [viewMode, setViewMode] = useState("table");
  const [sortCol, setSortCol] = useState("company_name");
  const [sortDir, setSortDir] = useState("asc");
  const [stageFilter, setStageFilter] = useState("all");
  const fileInputRef = useRef(null);

  const [newSponsor, setNewSponsor] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    address: "", website: "", industry: "", warmth: "cold", keywords: "", notes: "", status: "prospect",
  });

  const fetchAll = useCallback(async () => {
    try {
      // Fetch stages/templates first (public, no auth) to ensure pipeline renders
      const [stagesRes, templatesRes] = await Promise.all([
        axios.get(`${API}/sponsor-pipeline/stages`),
        axios.get(`${API}/sponsor-pipeline/templates`),
      ]);
      setStages(stagesRes.data.stages);
      setTiers(stagesRes.data.tiers);
      setTemplates(templatesRes.data);

      // Then fetch auth-required data — failures shouldn't break the page
      const [sponsorsRes, statsRes, settingsRes, unreadRes] = await Promise.allSettled([
        axios.get(`${API}/sponsor-pipeline`, { withCredentials: true }),
        axios.get(`${API}/sponsor-pipeline/stats/summary`, { withCredentials: true }),
        axios.get(`${API}/sponsor-pipeline/settings`, { withCredentials: true }),
        axios.get(`${API}/email/unread-sponsors`, { withCredentials: true }),
      ]);
      if (sponsorsRes.status === "fulfilled") setSponsors(sponsorsRes.value.data);
      if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
      if (settingsRes.status === "fulfilled") setOrgSettings(settingsRes.value.data);
      if (unreadRes.status === "fulfilled") setUnreadSponsors(unreadRes.value.data.unread_sponsors || {});
    } catch (err) {
      console.error("Failed to load sponsor pipeline:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredSponsors = useMemo(() => {
    return sponsors.filter((s) => {
      const matchSearch = !searchQuery ||
        s.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchWarmth = warmthFilter === "all" || s.warmth === warmthFilter;
      const matchStage = stageFilter === "all" || s.status === stageFilter;
      return matchSearch && matchWarmth && matchStage;
    });
  }, [sponsors, searchQuery, warmthFilter, stageFilter]);

  const sortedSponsorsForTable = useMemo(() => {
    return [...filteredSponsors].sort((a, b) => {
      let va = a[sortCol] || "";
      let vb = b[sortCol] || "";
      if (sortCol === "created_at" || sortCol === "last_contacted") { va = new Date(va || 0); vb = new Date(vb || 0); }
      else { va = String(va).toLowerCase(); vb = String(vb).toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredSponsors, sortCol, sortDir]);

  const toggleSponsorSort = (col) => {
    if (sortCol === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const renderSponsorSortIcon = (col) => {
    if (sortCol !== col) return <ArrowUpDown size={12} className="opacity-30" />;
    return sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const sponsorsForStage = useCallback(
    (stageId) => filteredSponsors.filter((s) => s.status === stageId),
    [filteredSponsors]
  );

  const handleStatusChange = async (sponsorId, newStatus) => {
    try {
      await axios.put(`${API}/sponsor-pipeline/${sponsorId}`, { status: newStatus }, { withCredentials: true });
      setSponsors((prev) => prev.map((s) => (s.sponsor_id === sponsorId ? { ...s, status: newStatus } : s)));
      toast.success("Sponsor moved");
    } catch { toast.error("Failed to move sponsor"); }
  };

  const handleDragStart = (e, sponsor) => { setDraggedSponsor(sponsor); e.dataTransfer.effectAllowed = "move"; e.target.style.opacity = "0.5"; };
  const handleDragEnd = (e) => { e.target.style.opacity = "1"; setDraggedSponsor(null); };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const handleDrop = async (e, stageId) => { e.preventDefault(); if (draggedSponsor && draggedSponsor.status !== stageId) await handleStatusChange(draggedSponsor.sponsor_id, stageId); };

  const handleAddSponsor = async () => {
    if (!newSponsor.company_name.trim()) { toast.error("Company name required"); return; }
    try {
      const res = await axios.post(`${API}/sponsor-pipeline`, newSponsor, { withCredentials: true });
      setSponsors((prev) => [res.data, ...prev]);
      setShowAddForm(false);
      setNewSponsor({ company_name: "", contact_name: "", email: "", phone: "", address: "", website: "", industry: "", warmth: "cold", keywords: "", notes: "", status: "prospect" });
      toast.success("Sponsor added to pipeline");
      // Refresh stats
      try {
        const statsRes = await axios.get(`${API}/sponsor-pipeline/stats/summary`, { withCredentials: true });
        setStats(statsRes.data);
      } catch { /* stats refresh is non-critical */ }
    } catch { toast.error("Failed to add sponsor"); }
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/sponsor-pipeline/import`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.message);
      fetchAll();
      setShowImport(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      await axios.post(`${API}/sponsor-pipeline/bulk-delete`, { sponsor_ids: [...selectedIds] }, { withCredentials: true });
      toast.success(`${selectedIds.size} sponsor(s) deleted`);
      setSelectedIds(new Set());
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const handleBulkEmail = async () => {
    if (selectedIds.size === 0 || !bulkTemplateId) { toast.error("Select sponsors and a template"); return; }
    setBulkSending(true);
    try {
      const res = await axios.post(`${API}/sponsor-pipeline/bulk-email`, {
        sponsor_ids: [...selectedIds],
        template_id: bulkTemplateId,
        attach_deck: bulkAttachDeck,
      }, { withCredentials: true });
      const { sent, failed, skipped } = res.data;
      toast.success(`Sent: ${sent}, Failed: ${failed}, Skipped: ${skipped} (no email)`);
      setBulkEmailOpen(false);
      setBulkTemplateId("");
      setSelectedIds(new Set());
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk send failed"); }
    finally { setBulkSending(false); }
  };

  const handleSponsorClick = (sponsor) => { setSelectedSponsor(sponsor); setSheetOpen(true); };

  const handleUpdateField = async (field, value) => {
    if (!selectedSponsor) return;
    try {
      const res = await axios.put(`${API}/sponsor-pipeline/${selectedSponsor.sponsor_id}`, { [field]: value }, { withCredentials: true });
      setSelectedSponsor(res.data);
      setSponsors((prev) => prev.map((s) => (s.sponsor_id === selectedSponsor.sponsor_id ? res.data : s)));
    } catch { toast.error("Update failed"); }
  };

  const handleAddNote = async (text) => {
    if (!selectedSponsor || !text.trim()) return;
    try {
      await axios.post(`${API}/sponsor-pipeline/${selectedSponsor.sponsor_id}/notes`, { text }, { withCredentials: true });
      const res = await axios.get(`${API}/sponsor-pipeline/${selectedSponsor.sponsor_id}`, { withCredentials: true });
      setSelectedSponsor(res.data);
    } catch { toast.error("Failed to add note"); }
  };

  const handleDeleteSponsor = async () => {
    if (!selectedSponsor) return;
    try {
      await axios.delete(`${API}/sponsor-pipeline/${selectedSponsor.sponsor_id}`, { withCredentials: true });
      setSponsors((prev) => prev.filter((s) => s.sponsor_id !== selectedSponsor.sponsor_id));
      setSheetOpen(false); setSelectedSponsor(null);
      toast.success("Sponsor deleted");
    } catch { toast.error("Delete failed"); }
  };

  const toggleStageCollapse = (stageId) => setCollapsedStages((prev) => ({ ...prev, [stageId]: !prev[stageId] }));

  if (loading) {
    return (<div className="flex items-center justify-center py-20" data-testid="sponsor-pipeline-loading"><Loader2 className="animate-spin text-cfc-primary" size={32} /></div>);
  }

  return (
    <div data-testid="sponsor-pipeline">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary" data-testid="sponsor-pipeline-title">Sponsor Pipeline</h1>
          {stats && (
            <p className="text-sm text-cfc-text-muted mt-1" data-testid="sponsor-pipeline-stats">
              {stats.total} total &middot; {stats.by_warmth?.hot || 0} hot &middot; {stats.by_warmth?.warm || 0} warm &middot; {stats.by_warmth?.cold || 0} cold
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex items-center border border-cfc-border rounded-sm overflow-hidden" data-testid="sponsor-view-toggle">
            <button onClick={() => setViewMode("table")} className={`p-2 ${viewMode === "table" ? "bg-cfc-primary text-white" : "text-cfc-text-muted hover:text-cfc-primary"}`} data-testid="sponsor-view-table-btn"><List size={16} /></button>
            <button onClick={() => setViewMode("kanban")} className={`p-2 ${viewMode === "kanban" ? "bg-cfc-primary text-white" : "text-cfc-text-muted hover:text-cfc-primary"}`} data-testid="sponsor-view-kanban-btn"><LayoutGrid size={16} /></button>
          </div>
          <Button size="sm" variant="outline" className="border-cfc-border text-cfc-text-muted hover:text-cfc-primary" onClick={() => setShowSettings(true)} data-testid="sponsor-settings-btn">
            <Settings size={16} className="mr-1.5" /> Settings
          </Button>
          <Button size="sm" variant="outline" className="border-cfc-primary text-cfc-primary hover:bg-cfc-primary hover:text-white" onClick={() => setShowImport(true)} data-testid="import-sponsors-btn">
            <Upload size={16} className="mr-1.5" /> Import List
          </Button>
          <Button className="bg-cfc-primary text-white hover:bg-cfc-primary/90 px-5 py-2 text-sm font-bold" onClick={() => setShowAddForm(true)} data-testid="add-sponsor-btn">
            <Plus size={18} className="mr-1.5" /> Add Sponsor
          </Button>
        </div>
      </div>

      {/* Filters + Select All */}
      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-cfc-text-muted" />
          <Input placeholder="Search sponsors..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 border-cfc-border" data-testid="sponsor-search-input" />
        </div>
        <div className="flex items-center bg-cfc-cream-alt rounded-sm overflow-hidden border border-cfc-border" data-testid="warmth-filter">
          {[{ id: "all", label: "All" }, { id: "cold", label: "Cold", icon: Snowflake }, { id: "warm", label: "Warm", icon: Thermometer }, { id: "hot", label: "Hot", icon: Flame }].map((f) => (
            <button key={f.id} onClick={() => setWarmthFilter(f.id)} className={`flex items-center gap-1 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${warmthFilter === f.id ? "bg-cfc-primary text-white" : "text-cfc-text-muted hover:text-cfc-primary"}`} data-testid={`warmth-filter-${f.id}`}>
              {f.icon && <f.icon size={12} />}{f.label}
            </button>
          ))}
        </div>
        {viewMode === "table" && stages.length > 0 && (
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="h-9 px-3 text-xs font-bold border border-cfc-border rounded-sm bg-white text-cfc-primary" data-testid="stage-filter">
            <option value="all">All Stages</option>
            {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        )}
        <span className="text-xs text-cfc-text-muted">{filteredSponsors.length} sponsor{filteredSponsors.length !== 1 ? "s" : ""}</span>
        <button
          onClick={() => {
            const allVisible = filteredSponsors.map((s) => s.sponsor_id);
            const allSelected = allVisible.every((id) => selectedIds.has(id));
            if (allSelected) {
              setSelectedIds(new Set());
            } else {
              setSelectedIds(new Set(allVisible));
            }
          }}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border transition-colors ${
            filteredSponsors.length > 0 && filteredSponsors.every((s) => selectedIds.has(s.sponsor_id))
              ? "bg-cfc-primary text-white border-cfc-primary"
              : "border-cfc-border text-cfc-text-muted hover:text-cfc-primary hover:border-cfc-primary"
          }`}
          data-testid="select-all-sponsors-btn"
        >
          <input
            type="checkbox"
            checked={filteredSponsors.length > 0 && filteredSponsors.every((s) => selectedIds.has(s.sponsor_id))}
            readOnly
            className="accent-white pointer-events-none"
          />
          Select All ({filteredSponsors.length})
        </button>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="mb-4 p-3 bg-cfc-cream-alt border border-cfc-border rounded-sm space-y-3" data-testid="bulk-actions-bar">
          <div className="flex items-center gap-4">
            <span className="text-sm font-bold text-cfc-primary">{selectedIds.size} sponsor{selectedIds.size !== 1 ? "s" : ""} selected</span>
            <Button size="sm" className="bg-cfc-primary text-white hover:bg-cfc-primary/90" onClick={() => setBulkEmailOpen(!bulkEmailOpen)} data-testid="bulk-email-toggle-btn">
              <Send size={14} className="mr-1.5" /> Send Email
            </Button>
            <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={handleBulkDelete} data-testid="bulk-delete-sponsors-btn">
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
            <Button size="sm" variant="ghost" className="text-cfc-text-muted" onClick={() => { setSelectedIds(new Set()); setBulkEmailOpen(false); }}>Clear</Button>
          </div>

          {bulkEmailOpen && (
            <div className="bg-white border border-cfc-border rounded-sm p-4 space-y-3" data-testid="bulk-email-panel">
              <p className="text-sm text-cfc-text-muted">
                Choose a template to send to <strong>{selectedIds.size}</strong> selected sponsor{selectedIds.size !== 1 ? "s" : ""}. Each email will be personalized with the sponsor{"'"}s details.
              </p>
              <div>
                <label className="text-xs font-bold text-cfc-text-muted uppercase mb-1.5 block">Email Template</label>
                <select
                  value={bulkTemplateId}
                  onChange={(e) => setBulkTemplateId(e.target.value)}
                  className="w-full border border-cfc-border rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cfc-primary/20"
                  data-testid="bulk-template-select"
                >
                  <option value="">Select a template...</option>
                  {templates.map((t) => (
                    <option key={t.template_id} value={t.template_id}>
                      {t.name} — {t.subject?.slice(0, 50)}
                    </option>
                  ))}
                </select>
              </div>
              {bulkTemplateId && (
                <div className="text-xs text-cfc-text-muted bg-cfc-cream-alt p-2 rounded">
                  Variables like {"{company_name}"}, {"{contact_name}"} will be replaced per sponsor.
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer" data-testid="attach-deck-toggle">
                <input type="checkbox" checked={bulkAttachDeck} onChange={(e) => setBulkAttachDeck(e.target.checked)} className="accent-cfc-primary" />
                <FileText size={14} className="text-cfc-primary" />
                <span className="text-xs font-medium text-cfc-primary">Attach Sponsorship Deck (PDF)</span>
              </label>
              <div className="flex gap-2">
                <Button
                  className="bg-cfc-primary text-white hover:bg-cfc-primary/90 flex-1"
                  onClick={handleBulkEmail}
                  disabled={bulkSending || !bulkTemplateId}
                  data-testid="bulk-email-send-btn"
                >
                  {bulkSending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
                  Send to {selectedIds.size} Sponsor{selectedIds.size !== 1 ? "s" : ""}
                </Button>
                <Button variant="outline" onClick={() => setBulkEmailOpen(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {sponsors.length === 0 && (
        <div className="bg-white border border-cfc-border rounded-sm p-8 mb-6 text-center" data-testid="sponsor-empty-state">
          <Building2 size={40} className="mx-auto text-cfc-text-muted mb-3" />
          <h2 className="font-heading text-xl font-bold text-cfc-primary mb-2">No Sponsors Yet</h2>
          <p className="text-sm text-cfc-text-muted mb-5 max-w-md mx-auto">
            Get started by importing your sponsor outreach list or adding sponsors manually. Once added, drag and drop them through pipeline stages to track your outreach progress.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button className="bg-cfc-primary text-white hover:bg-cfc-primary/90" onClick={() => setShowImport(true)} data-testid="empty-import-btn">
              <Upload size={16} className="mr-1.5" /> Import Excel / CSV
            </Button>
            <Button variant="outline" className="border-cfc-primary text-cfc-primary" onClick={() => setShowAddForm(true)} data-testid="empty-add-btn">
              <Plus size={16} className="mr-1.5" /> Add Manually
            </Button>
          </div>
        </div>
      )}

      {/* TABLE VIEW */}
      {viewMode === "table" && sponsors.length > 0 && (
        <div className="bg-white border border-cfc-border rounded-sm overflow-x-auto mb-6" data-testid="sponsors-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cfc-border bg-cfc-cream-alt">
                <th className="w-8 px-3 py-2.5">
                  <input type="checkbox" onChange={(e) => { if (e.target.checked) setSelectedIds(new Set(sortedSponsorsForTable.map((s) => s.sponsor_id))); else setSelectedIds(new Set()); }} className="accent-cfc-primary" />
                </th>
                {[
                  { key: "company_name", label: "Company" },
                  { key: "contact_name", label: "Contact" },
                  { key: "email", label: "Email" },
                  { key: "status", label: "Stage" },
                  { key: "warmth", label: "Warmth" },
                  { key: "industry", label: "Industry" },
                  { key: "created_at", label: "Added" },
                ].map((col) => (
                  <th key={col.key} className="px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-cfc-text-muted cursor-pointer hover:text-cfc-primary select-none" onClick={() => toggleSponsorSort(col.key)}>
                    <span className="inline-flex items-center gap-1">{col.label} {renderSponsorSortIcon(col.key)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedSponsorsForTable.map((sponsor) => {
                const warmth = WARMTH_CONFIG[sponsor.warmth] || WARMTH_CONFIG.cold;
                const stage = stages.find((s) => s.id === sponsor.status);
                return (
                  <tr
                    key={sponsor.sponsor_id}
                    className={`border-b border-cfc-border/50 hover:bg-cfc-cream-alt/50 cursor-pointer transition-colors ${selectedIds.has(sponsor.sponsor_id) ? "bg-blue-50" : ""}`}
                    onClick={() => handleSponsorClick(sponsor)}
                    data-testid={`sponsor-row-${sponsor.sponsor_id}`}
                  >
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(sponsor.sponsor_id)} onChange={(e) => { e.stopPropagation(); setSelectedIds((prev) => { const n = new Set(prev); n.has(sponsor.sponsor_id) ? n.delete(sponsor.sponsor_id) : n.add(sponsor.sponsor_id); return n; }); }} className="accent-cfc-primary" />
                    </td>
                    <td className="px-3 py-2.5 font-medium text-cfc-primary whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {sponsor.company_name}
                        {unreadSponsors[sponsor.sponsor_id] && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-red-500 text-white"><Mail size={8} /> {unreadSponsors[sponsor.sponsor_id]}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-cfc-text-muted">{sponsor.contact_name || "—"}</td>
                    <td className="px-3 py-2.5 text-cfc-text-muted truncate max-w-[180px]">{sponsor.email || "—"}</td>
                    <td className="px-3 py-2.5">
                      {stage && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full text-white" style={{ backgroundColor: stage.color }}>
                          {stage.label}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded border ${warmth.bg}`}>
                        <warmth.icon size={10} className={warmth.color} /> {warmth.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-cfc-text-muted text-xs">{sponsor.industry || "—"}</td>
                    <td className="px-3 py-2.5 text-cfc-text-muted text-xs whitespace-nowrap">{sponsor.created_at ? new Date(sponsor.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                  </tr>
                );
              })}
              {sortedSponsorsForTable.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-12 text-center text-cfc-text-muted">No sponsors match your filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Kanban Board */}
      {viewMode === "kanban" && (
      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="sponsor-kanban">
        {stages.map((stage) => {
          const stageSponsors = sponsorsForStage(stage.id);
          const isCollapsed = collapsedStages[stage.id];
          return (
            <div key={stage.id} className={`flex-shrink-0 ${isCollapsed ? "w-12" : "w-72"} bg-cfc-cream-alt rounded-sm transition-all`} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, stage.id)} data-testid={`pipeline-stage-${stage.id}`}>
              {isCollapsed ? (
                <button onClick={() => toggleStageCollapse(stage.id)} className="w-full h-full min-h-[200px] flex flex-col items-center pt-4 gap-2" data-testid={`expand-stage-${stage.id}`}>
                  <ChevronRight size={16} className="text-cfc-text-muted" />
                  <span className="text-xs font-bold uppercase tracking-wider text-cfc-text-muted" style={{ writingMode: "vertical-rl" }}>{stage.label}</span>
                  <span className="text-[10px] font-bold rounded-full px-1.5 py-0.5 text-white" style={{ backgroundColor: stage.color }}>{stageSponsors.length}</span>
                </button>
              ) : (
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <button onClick={() => toggleStageCollapse(stage.id)} className="text-cfc-text-muted hover:text-cfc-primary"><ChevronDown size={14} /></button>
                    {stageSponsors.length > 0 && (
                      <input
                        type="checkbox"
                        checked={stageSponsors.length > 0 && stageSponsors.every((s) => selectedIds.has(s.sponsor_id))}
                        onChange={() => {
                          const ids = stageSponsors.map((s) => s.sponsor_id);
                          const allSelected = ids.every((id) => selectedIds.has(id));
                          setSelectedIds((prev) => {
                            const n = new Set(prev);
                            ids.forEach((id) => allSelected ? n.delete(id) : n.add(id));
                            return n;
                          });
                        }}
                        className="accent-cfc-primary cursor-pointer"
                        title={`Select all in ${stage.label}`}
                        data-testid={`select-stage-${stage.id}`}
                      />
                    )}
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                    <h3 className="font-heading text-sm font-bold text-cfc-primary flex-1 truncate">{stage.label}</h3>
                    <span className="text-[11px] font-bold rounded-full px-2 py-0.5 text-white" style={{ backgroundColor: stage.color }}>{stageSponsors.length}</span>
                    <button
                      onClick={() => { setNewSponsor((p) => ({ ...p, status: stage.id })); setShowAddForm(true); }}
                      className="w-6 h-6 flex items-center justify-center rounded-sm bg-cfc-primary/10 text-cfc-primary hover:bg-cfc-primary hover:text-white transition-colors"
                      title={`Add sponsor to ${stage.label}`}
                      data-testid={`add-to-stage-${stage.id}`}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <div className="space-y-2 min-h-[60px]">
                    {stageSponsors.map((sponsor) => (
                      <SponsorCard key={sponsor.sponsor_id} sponsor={sponsor} selected={selectedIds.has(sponsor.sponsor_id)}
                        unreadCount={unreadSponsors[sponsor.sponsor_id] || 0}
                        onToggleSelect={(e) => { e.stopPropagation(); setSelectedIds((prev) => { const n = new Set(prev); n.has(sponsor.sponsor_id) ? n.delete(sponsor.sponsor_id) : n.add(sponsor.sponsor_id); return n; }); }}
                        onClick={() => handleSponsorClick(sponsor)} onDragStart={(e) => handleDragStart(e, sponsor)} onDragEnd={handleDragEnd} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Add Sponsor Sheet */}
      <Sheet open={showAddForm} onOpenChange={(open) => { setShowAddForm(open); if (!open) setNewSponsor((p) => ({ ...p, status: "prospect" })); }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading text-cfc-primary">Add Sponsor Manually</SheetTitle></SheetHeader>
          <p className="text-sm text-cfc-text-muted mt-1 mb-4">Add a new sponsor lead to your pipeline. Fill in the details below.</p>
          <div className="space-y-3">
            <Input placeholder="Company / Business Name *" value={newSponsor.company_name} onChange={(e) => setNewSponsor((p) => ({ ...p, company_name: e.target.value }))} data-testid="new-sponsor-company" />
            <Input placeholder="Contact Person Name" value={newSponsor.contact_name} onChange={(e) => setNewSponsor((p) => ({ ...p, contact_name: e.target.value }))} data-testid="new-sponsor-contact" />
            <Input placeholder="Email Address" type="email" value={newSponsor.email} onChange={(e) => setNewSponsor((p) => ({ ...p, email: e.target.value }))} data-testid="new-sponsor-email" />
            <Input placeholder="Phone Number" value={newSponsor.phone} onChange={(e) => setNewSponsor((p) => ({ ...p, phone: e.target.value }))} data-testid="new-sponsor-phone" />
            <Input placeholder="Address" value={newSponsor.address} onChange={(e) => setNewSponsor((p) => ({ ...p, address: e.target.value }))} data-testid="new-sponsor-address" />
            <Input placeholder="Website URL" value={newSponsor.website} onChange={(e) => setNewSponsor((p) => ({ ...p, website: e.target.value }))} data-testid="new-sponsor-website" />
            <Input placeholder="Industry / Community" value={newSponsor.industry} onChange={(e) => setNewSponsor((p) => ({ ...p, industry: e.target.value }))} data-testid="new-sponsor-industry" />
            <Input placeholder="Keywords (comma separated)" value={newSponsor.keywords} onChange={(e) => setNewSponsor((p) => ({ ...p, keywords: e.target.value }))} data-testid="new-sponsor-keywords" />
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase mb-1 block">Pipeline Stage</label>
              <div className="flex flex-wrap gap-1.5">
                {stages.map((s) => (
                  <button key={s.id} onClick={() => setNewSponsor((p) => ({ ...p, status: s.id }))}
                    className={`px-2.5 py-1 text-[11px] font-bold rounded-sm transition-colors ${newSponsor.status === s.id ? "text-white" : "bg-gray-100 text-cfc-text-muted hover:bg-gray-200"}`}
                    style={newSponsor.status === s.id ? { backgroundColor: s.color } : {}}
                    data-testid={`new-sponsor-stage-${s.id}`}
                  >{s.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase mb-1 block">Warmth</label>
              <div className="flex gap-2">
                {["cold", "warm", "hot"].map((w) => { const cfg = WARMTH_CONFIG[w]; return (
                  <button key={w} onClick={() => setNewSponsor((p) => ({ ...p, warmth: w }))} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-bold border transition-colors ${newSponsor.warmth === w ? `${cfg.bg} ${cfg.color} border` : "border-cfc-border text-cfc-text-muted"}`} data-testid={`new-sponsor-warmth-${w}`}>
                    <cfg.icon size={14} /> {cfg.label}
                  </button>
                ); })}
              </div>
            </div>
            <Textarea placeholder="Notes (optional)" value={newSponsor.notes} onChange={(e) => setNewSponsor((p) => ({ ...p, notes: e.target.value }))} data-testid="new-sponsor-notes" />
            <Button className="w-full bg-cfc-primary text-white font-bold py-2.5" onClick={handleAddSponsor} data-testid="save-new-sponsor-btn">
              <Plus size={16} className="mr-1.5" /> Add Sponsor
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Import Sheet */}
      <Sheet open={showImport} onOpenChange={setShowImport}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader><SheetTitle className="font-heading text-cfc-primary">Import Sponsors</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-4">
            <p className="text-sm text-cfc-text-muted">Upload an Excel (.xlsx) or CSV file with columns: Name, Email, Phone, Address, Website, Community, Keywords, Warmth Score.</p>
            <div className="border-2 border-dashed border-cfc-border rounded-sm p-8 text-center">
              <Upload size={32} className="mx-auto text-cfc-text-muted mb-3" />
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileImport} className="block w-full text-sm text-cfc-text-muted file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-sm file:font-bold file:bg-cfc-primary file:text-white cursor-pointer" data-testid="import-file-input" />
            </div>
            {importing && <div className="flex items-center gap-2 text-sm text-cfc-primary"><Loader2 size={16} className="animate-spin" /> Importing...</div>}
          </div>
        </SheetContent>
      </Sheet>

      {/* Settings Sheet */}
      <Sheet open={showSettings} onOpenChange={setShowSettings}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading text-cfc-primary">Sponsor Pipeline Settings</SheetTitle></SheetHeader>
          <SponsorSettingsEditor settings={orgSettings} onSaved={fetchAll} />
        </SheetContent>
      </Sheet>

      {/* Sponsor Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) setSelectedSponsor(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0">
          <SheetHeader className="sr-only"><SheetTitle>Sponsor Details</SheetTitle></SheetHeader>
          {selectedSponsor && (
            <SponsorDetail
              sponsor={selectedSponsor}
              stages={stages}
              tiers={tiers}
              templates={templates}
              onUpdateField={handleUpdateField}
              onAddNote={handleAddNote}
              onDelete={handleDeleteSponsor}
              onRefresh={async () => {
                const res = await axios.get(`${API}/sponsor-pipeline/${selectedSponsor.sponsor_id}`, { withCredentials: true });
                setSelectedSponsor(res.data);
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

/* ==================== Sponsor Card ==================== */
const SponsorCard = ({ sponsor, selected, unreadCount, onToggleSelect, onClick, onDragStart, onDragEnd }) => {
  const warmth = WARMTH_CONFIG[sponsor.warmth] || WARMTH_CONFIG.cold;
  const WarmthIcon = warmth.icon;
  return (
    <Card className={`cursor-grab border-cfc-border hover:border-cfc-accent active:cursor-grabbing transition-all ${selected ? "ring-2 ring-red-300 border-red-300" : ""}`}
      onClick={onClick} draggable onDragStart={onDragStart} onDragEnd={onDragEnd} data-testid={`sponsor-card-${sponsor.sponsor_id}`}>
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <input type="checkbox" checked={selected} onChange={onToggleSelect} onClick={(e) => e.stopPropagation()} className="mt-1 flex-shrink-0 accent-red-500 cursor-pointer" data-testid={`select-sponsor-${sponsor.sponsor_id}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <h4 className="font-medium text-sm text-cfc-primary truncate">{sponsor.company_name}</h4>
              <WarmthIcon size={12} className={warmth.color} />
            </div>
            {sponsor.contact_name && <p className="text-xs text-cfc-text-muted truncate">{sponsor.contact_name}</p>}
            {sponsor.email && <div className="flex items-center gap-1 text-xs text-cfc-text-muted mt-1"><Mail size={10} /><span className="truncate">{sponsor.email}</span></div>}
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {sponsor.emails_sent?.length > 0 && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded bg-cfc-primary/10 text-cfc-primary">
                  <Send size={8} /> {sponsor.emails_sent.length} sent
                </span>
              )}
              {unreadCount > 0 && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-red-500 text-white" data-testid={`unread-badge-${sponsor.sponsor_id}`}>
                  <ArrowDownLeft size={8} /> {unreadCount} new
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ==================== Sponsor Detail Panel ==================== */
const SponsorDetail = ({ sponsor, stages, tiers, templates, onUpdateField, onAddNote, onDelete, onRefresh }) => {
  const [noteText, setNoteText] = useState("");
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [activeTab, setActiveTab] = useState("info");
  const warmth = WARMTH_CONFIG[sponsor.warmth] || WARMTH_CONFIG.cold;
  const WarmthIcon = warmth.icon;

  const startEdit = (field, currentValue) => { setEditingField(field); setEditValue(currentValue || ""); };
  const saveEdit = () => { if (editingField) { onUpdateField(editingField, editValue); setEditingField(null); } };
  const handleNoteSubmit = () => { if (noteText.trim()) { onAddNote(noteText); setNoteText(""); } };

  const renderInfoRow = (Icon, label, field, value) => (
    <div key={field} className="flex items-start gap-3 py-2">
      <Icon size={14} className="text-cfc-text-muted mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-[10px] font-bold uppercase text-cfc-text-muted block">{label}</span>
        {editingField === field ? (
          <div className="flex gap-1 mt-0.5">
            <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} className="h-7 text-sm" autoFocus onKeyDown={(e) => e.key === "Enter" && saveEdit()} />
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={saveEdit}>Save</Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditingField(null)}><X size={12} /></Button>
          </div>
        ) : (
          <span className="text-sm text-cfc-primary cursor-pointer hover:underline break-all" onClick={() => startEdit(field, value)} data-testid={`sponsor-field-${field}`}>
            {value || <span className="text-cfc-text-muted italic">Click to add</span>}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="sponsor-detail-sheet">
      <div className="p-5 bg-cfc-primary text-white">
        <div className="flex items-center gap-2 mb-1"><Building2 size={18} /><h2 className="font-heading text-xl font-bold truncate">{sponsor.company_name}</h2></div>
        <div className="flex items-center gap-3 text-sm text-white/80">
          <span className="flex items-center gap-1"><WarmthIcon size={14} /> {warmth.label}</span>
          {sponsor.industry && <span className="flex items-center gap-1"><Tag size={12} /> {sponsor.industry}</span>}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0">
          {[{ id: "info", label: "Info" }, { id: "email", label: "Email Thread" }, { id: "templates", label: "Templates" }].map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="rounded-none border-b-2 border-transparent data-[state=active]:border-cfc-primary data-[state=active]:bg-transparent data-[state=active]:text-cfc-primary px-4 py-2.5 text-xs font-bold uppercase" data-testid={`sponsor-tab-${tab.id}`}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* INFO TAB */}
        <TabsContent value="info" className="p-5 space-y-5 mt-0">
          <div>
            <label className="text-[10px] font-bold uppercase text-cfc-text-muted block mb-2">Pipeline Stage</label>
            <div className="flex flex-wrap gap-1.5">
              {stages.map((s) => (
                <button key={s.id} onClick={() => onUpdateField("status", s.id)} className={`px-2.5 py-1 text-[11px] font-bold rounded-sm transition-colors ${sponsor.status === s.id ? "text-white" : "bg-gray-100 text-cfc-text-muted hover:bg-gray-200"}`} style={sponsor.status === s.id ? { backgroundColor: s.color } : {}} data-testid={`set-stage-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-cfc-text-muted block mb-2">Warmth</label>
            <div className="flex gap-2">
              {["cold", "warm", "hot"].map((w) => { const cfg = WARMTH_CONFIG[w]; return (
                <button key={w} onClick={() => onUpdateField("warmth", w)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-bold border transition-colors ${sponsor.warmth === w ? `${cfg.bg} ${cfg.color}` : "border-cfc-border text-cfc-text-muted"}`} data-testid={`set-warmth-${w}`}>
                  <cfg.icon size={14} /> {cfg.label}
                </button>
              ); })}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-cfc-text-muted block mb-2">Tier Interest</label>
            <div className="flex flex-wrap gap-1.5">
              {tiers.map((t) => (
                <button key={t.id} onClick={() => onUpdateField("tier_interest", t.id)} className={`px-2.5 py-1 text-[11px] font-bold rounded-sm border transition-colors ${sponsor.tier_interest === t.id ? "bg-cfc-primary text-white border-cfc-primary" : "border-cfc-border text-cfc-text-muted hover:border-cfc-primary"}`} data-testid={`set-tier-${t.id}`}>
                  {t.label} ({t.price})
                </button>
              ))}
            </div>
          </div>
          <Separator />
          <div>
            {renderInfoRow(Building2, "Company", "company_name", sponsor.company_name)}
            {renderInfoRow(Mail, "Email", "email", sponsor.email)}
            {renderInfoRow(Phone, "Phone", "phone", sponsor.phone)}
            {renderInfoRow(Globe, "Website", "website", sponsor.website)}
            {renderInfoRow(MapPin, "Address", "address", sponsor.address)}
            {renderInfoRow(Tag, "Keywords", "keywords", sponsor.keywords)}
          </div>
          <Separator />
          <div>
            <h3 className="text-xs font-bold uppercase text-cfc-text-muted mb-2 flex items-center gap-1.5"><Clock size={14} /> Activity & Notes</h3>
            <div className="flex gap-2 mb-3">
              <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..." className="text-sm" onKeyDown={(e) => e.key === "Enter" && handleNoteSubmit()} data-testid="sponsor-note-input" />
              <Button size="sm" className="bg-cfc-primary text-white" onClick={handleNoteSubmit} data-testid="add-sponsor-note-btn">Add</Button>
            </div>
            <div className="space-y-1.5 max-h-60 overflow-y-auto">
              {(sponsor.activity_log || []).slice().reverse().map((log, i) => (
                <div key={i} className="text-xs p-2 bg-white rounded-sm border border-cfc-border">
                  {log.action === "note" ? <p className="text-cfc-primary">{log.text}</p>
                    : log.action === "email_sent" ? <p className="text-cfc-primary">Sent: <span className="font-medium">{log.template}</span></p>
                    : log.action === "status_changed" ? <p className="text-cfc-primary">Moved from <span className="font-medium">{log.from}</span> to <span className="font-medium">{log.to}</span></p>
                    : log.action === "imported" ? <p className="text-cfc-primary">Imported from <span className="font-medium">{log.source}</span></p>
                    : <p className="text-cfc-primary capitalize">{log.action}</p>}
                  <p className="text-cfc-text-muted mt-0.5">{log.by} &middot; {new Date(log.at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
          <Separator />
          <Button variant="outline" className="w-full text-red-500 border-red-300 hover:bg-red-50" onClick={onDelete} data-testid="delete-sponsor-btn"><Trash2 size={14} className="mr-1.5" /> Delete Sponsor</Button>
        </TabsContent>

        {/* EMAIL THREAD TAB */}
        <TabsContent value="email" className="p-5 mt-0">
          <SponsorEmailThread sponsorId={sponsor.sponsor_id} sponsorEmail={sponsor.email} />
        </TabsContent>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates" className="p-5 mt-0">
          <SponsorTemplatePanel sponsorId={sponsor.sponsor_id} templates={templates} onRefresh={onRefresh} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

/* ==================== Sponsor Email Thread ==================== */
const SponsorEmailThread = ({ sponsorId, sponsorEmail }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sponsor-pipeline/email/conversations/${sponsorId}`, { withCredentials: true });
      setMessages(res.data.messages || []);
    } catch (err) { console.error("Failed to fetch sponsor email thread:", err); }
    finally { setLoading(false); }
  }, [sponsorId]);

  useEffect(() => {
    fetchMessages();
    axios.post(`${API}/sponsor-pipeline/email/mark-read/${sponsorId}`, {}, { withCredentials: true }).catch(() => {});
    const interval = setInterval(fetchMessages, 15000);
    return () => clearInterval(interval);
  }, [fetchMessages, sponsorId]);

  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [messages]);

  const sendEmail = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body required"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/sponsor-pipeline/email/send/${sponsorId}`, { subject: subject.trim(), body: body.trim() }, { withCredentials: true });
      setSubject(""); setBody(""); setShowCompose(false);
      await fetchMessages();
      toast.success("Email sent");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send email"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="sponsor-email-thread">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-cfc-primary/10 flex items-center justify-center"><Mail size={13} className="text-cfc-primary" /></div>
          <span className="font-semibold text-cfc-primary text-sm">Email Thread</span>
          {messages.length > 0 && <span className="text-[10px] bg-cfc-primary/10 text-cfc-primary font-medium px-1.5 py-0.5 rounded-full">{messages.length}</span>}
        </div>
        <Button size="sm" variant={showCompose ? "ghost" : "default"} className={showCompose ? "text-xs text-gray-500 h-7" : "text-xs h-7 bg-cfc-primary hover:bg-cfc-primary/90"} onClick={() => setShowCompose(!showCompose)} data-testid="sponsor-compose-toggle">
          {showCompose ? "Cancel" : <><Pencil size={12} className="mr-1" /> New Email</>}
        </Button>
      </div>

      {!sponsorEmail && <p className="text-xs text-cfc-text-muted italic">No email address — add one to send emails.</p>}

      {showCompose && sponsorEmail && (
        <div className="rounded-lg border border-cfc-primary/20 mb-3 bg-white shadow-sm overflow-hidden" data-testid="sponsor-email-compose">
          <div className="bg-cfc-primary/5 px-3 py-2 border-b border-cfc-primary/10">
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (required)" className="w-full bg-transparent text-sm font-medium text-cfc-primary placeholder:text-gray-400 focus:outline-none" autoFocus data-testid="sponsor-email-subject" />
          </div>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your message..." className="border-0 rounded-none min-h-[120px] text-sm focus-visible:ring-0" data-testid="sponsor-email-body" />
          <div className="flex justify-end items-center px-3 py-2 border-t border-gray-100 bg-gray-50/50">
            <Button size="sm" onClick={sendEmail} disabled={sending} className="h-7 bg-cfc-primary hover:bg-cfc-primary/90 text-xs px-4" data-testid="sponsor-email-send-btn">
              {sending ? <Loader2 size={13} className="animate-spin mr-1" /> : <Send size={13} className="mr-1" />} Send
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-gray-300" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 px-4"><Mail size={24} className="mx-auto mb-2 text-gray-300" /><p className="text-xs text-gray-400">No email conversation yet</p></div>
          ) : (
            <div className="divide-y divide-gray-100">
              {messages.map((msg, idx) => {
                const isOutbound = msg.direction === "outbound";
                return (
                  <div key={msg.message_id || idx} className="group hover:bg-gray-50/50 transition-colors" data-testid={`sponsor-email-msg-${msg.direction}`}>
                    <div className="flex items-start gap-2.5 px-3.5 pt-3 pb-1">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${isOutbound ? "bg-emerald-100" : "bg-blue-100"}`}>
                        {isOutbound ? <ArrowUpRight size={12} className="text-emerald-600" /> : <ArrowDownLeft size={12} className="text-blue-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-xs font-semibold text-gray-800">{isOutbound ? "You" : (msg.from_email || "Unknown")}</span>
                          <span className="text-[10px] text-gray-400 shrink-0 tabular-nums">{formatTime(msg.timestamp)}</span>
                        </div>
                        {msg.subject && (idx === 0 || msg.subject !== messages[idx - 1]?.subject) && (
                          <p className="text-[11px] text-gray-500 font-medium mt-0.5 truncate">{msg.subject}</p>
                        )}
                      </div>
                    </div>
                    <div className="pl-12 pr-3.5 pb-3 text-[13px] text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {msg.body || msg.body_text || ""}
                      {isOutbound && msg.event_types && msg.event_types.length > 0 && (
                        <div className="flex items-center gap-2 mt-1.5" data-testid="sponsor-email-events">
                          {msg.event_types.includes("delivered") && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-green-600"><Check size={10} /> Delivered</span>
                          )}
                          {msg.event_types.includes("open") && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-blue-600"><Eye size={10} /> Opened</span>
                          )}
                          {msg.event_types.includes("click") && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-purple-600"><MousePointerClick size={10} /> Clicked</span>
                          )}
                          {(msg.event_types.includes("bounce") || msg.event_types.includes("dropped")) && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-red-600"><AlertTriangle size={10} /> Bounced</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  );
};

/* ==================== Sponsor Template Panel ==================== */
const SponsorTemplatePanel = ({ sponsorId, templates, onRefresh }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadPreview = async (template) => {
    setSelectedTemplate(template);
    setEditMode(false);
    try {
      const res = await axios.get(`${API}/sponsor-pipeline/${sponsorId}/export-email?template_id=${template.template_id}`, { withCredentials: true });
      setPreview(res.data);
    } catch { toast.error("Failed to load preview"); }
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplate) return;
    setSending(true);
    try {
      await axios.post(`${API}/sponsor-pipeline/email/send/${sponsorId}`, { template_id: selectedTemplate.template_id }, { withCredentials: true });
      toast.success("Email sent via template");
      onRefresh();
      setSelectedTemplate(null); setPreview(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  const startEdit = () => {
    if (!selectedTemplate) return;
    setEditMode(true);
    setEditSubject(selectedTemplate.subject);
    setEditBody(selectedTemplate.body);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      await axios.put(`${API}/sponsor-pipeline/templates/${selectedTemplate.template_id}`, { subject: editSubject, body: editBody }, { withCredentials: true });
      toast.success("Template saved");
      setEditMode(false);
      // Reload templates
      const res = await axios.get(`${API}/sponsor-pipeline/templates`);
      const updated = res.data.find((t) => t.template_id === selectedTemplate.template_id);
      if (updated) { setSelectedTemplate(updated); loadPreview(updated); }
    } catch { toast.error("Failed to save template"); }
    finally { setSaving(false); }
  };

  const handleResetTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      await axios.post(`${API}/sponsor-pipeline/templates/${selectedTemplate.template_id}/reset`, {}, { withCredentials: true });
      toast.success("Template reset to default");
      const res = await axios.get(`${API}/sponsor-pipeline/templates`);
      const updated = res.data.find((t) => t.template_id === selectedTemplate.template_id);
      if (updated) { setSelectedTemplate(updated); loadPreview(updated); }
      setEditMode(false);
    } catch { toast.error("Failed to reset"); }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  if (selectedTemplate) {
    return (
      <div data-testid="template-detail">
        <Button variant="ghost" size="sm" className="mb-3 text-xs" onClick={() => { setSelectedTemplate(null); setPreview(null); setEditMode(false); }} data-testid="template-back-btn">
          <ChevronRight size={14} className="rotate-180 mr-1" /> Back to templates
        </Button>
        <h3 className="font-heading text-lg font-bold text-cfc-primary mb-1">{selectedTemplate.name}</h3>
        {selectedTemplate.category && <span className="text-[10px] uppercase font-bold text-cfc-text-muted bg-cfc-cream-alt px-2 py-0.5 rounded">{selectedTemplate.category}</span>}
        {!selectedTemplate.is_default && <span className="text-[10px] uppercase font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded ml-1">Edited</span>}

        <div className="mt-4 space-y-3">
          {editMode ? (
            <>
              <div>
                <label className="text-xs font-bold text-cfc-text-muted uppercase">Subject</label>
                <Input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} className="mt-1" data-testid="edit-template-subject" />
              </div>
              <div>
                <label className="text-xs font-bold text-cfc-text-muted uppercase">Body</label>
                <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} className="mt-1 min-h-[250px] text-sm font-mono" data-testid="edit-template-body" />
                <p className="text-[10px] text-cfc-text-muted mt-1">Variables: {"{company_name}"}, {"{contact_name}"}, {"{club_name}"}, {"{rep_name}"}, {"{rep_email}"}, {"{meeting_link}"} (renders as hyperlink), {"{referral_name}"}</p>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1 bg-cfc-primary text-white" onClick={handleSaveTemplate} disabled={saving} data-testid="save-template-btn">
                  {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Save Template
                </Button>
                <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                <Button variant="ghost" className="text-cfc-text-muted" onClick={handleResetTemplate} data-testid="reset-template-btn"><RotateCcw size={14} /></Button>
              </div>
            </>
          ) : preview ? (
            <>
              <div>
                <label className="text-xs font-bold text-cfc-text-muted uppercase">Subject (Preview)</label>
                <div className="flex items-center gap-2 mt-1">
                  <Input value={preview.subject} readOnly className="bg-cfc-cream-alt" data-testid="email-preview-subject" />
                  <Button variant="ghost" size="icon" onClick={() => copyToClipboard(preview.subject)} data-testid="copy-subject-btn"><Copy size={16} /></Button>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-cfc-text-muted uppercase">Body (Preview)</label>
                <div className="relative mt-1">
                  <Textarea value={preview.body} readOnly className="bg-cfc-cream-alt min-h-[200px] text-sm" data-testid="email-preview-body" />
                  <Button variant="ghost" size="icon" className="absolute top-2 right-2" onClick={() => copyToClipboard(preview.body)} data-testid="copy-body-btn"><Copy size={16} /></Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1 bg-cfc-primary text-white" onClick={handleSendTemplate} disabled={sending} data-testid="send-template-btn">
                  {sending ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />} Send Email
                </Button>
                <Button variant="outline" className="border-cfc-primary text-cfc-primary" onClick={startEdit} data-testid="edit-template-btn">
                  <Pencil size={14} className="mr-1" /> Edit
                </Button>
              </div>
            </>
          ) : (
            <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-gray-300" /></div>
          )}
        </div>
      </div>
    );
  }

  // Template list
  const categories = [...new Set(templates.map((t) => t.category || "other"))];
  return (
    <div data-testid="template-list">
      <h3 className="text-xs font-bold uppercase text-cfc-text-muted mb-3 flex items-center gap-1.5"><FileText size={14} /> Email Templates ({templates.length})</h3>
      {categories.map((cat) => (
        <div key={cat} className="mb-4">
          <h4 className="text-[10px] font-bold uppercase text-cfc-text-muted mb-1.5 tracking-wider">{cat}</h4>
          <div className="space-y-1.5">
            {templates.filter((t) => (t.category || "other") === cat).map((t) => (
              <button key={t.template_id} onClick={() => loadPreview(t)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-sm border border-cfc-border hover:border-cfc-primary hover:bg-cfc-cream-alt transition-colors" data-testid={`template-btn-${t.template_id}`}>
                <Mail size={14} className="text-cfc-text-muted flex-shrink-0" />
                <span className="flex-1 truncate text-cfc-primary font-medium">{t.name}</span>
                {!t.is_default && <span className="text-[9px] text-amber-600 font-bold">EDITED</span>}
                <ChevronRight size={14} className="text-cfc-text-muted" />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ==================== Settings Editor ==================== */
const SponsorSettingsEditor = ({ settings, onSaved }) => {
  const defaultSettings = settings.find((s) => !s.org) || {};
  const [form, setForm] = useState({
    club_name: defaultSettings.club_name || "CVille United FC",
    rep_name: defaultSettings.rep_name || "",
    rep_email: defaultSettings.rep_email || "",
    meeting_link: defaultSettings.meeting_link || "",
    meeting_link_text: defaultSettings.meeting_link_text || "",
    from_email: defaultSettings.from_email || "",
    from_name: defaultSettings.from_name || "",
    tagline: defaultSettings.tagline || "",
    header_color: defaultSettings.header_color || "#0C2E3C",
    accent_color: defaultSettings.accent_color || "#C8E64E",
    website_url: defaultSettings.website_url || "",
    footer_location: defaultSettings.footer_location || "",
  });
  const [saving, setSaving] = useState(false);
  const [deck, setDeck] = useState(null);
  const [deckLoading, setDeckLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const deckInputRef = useRef(null);

  // Sync form when settings prop loads/changes
  useEffect(() => {
    const s = settings.find((item) => !item.org) || {};
    if (Object.keys(s).length > 0) {
      setForm({
        club_name: s.club_name || "CVille United FC",
        rep_name: s.rep_name || "",
        rep_email: s.rep_email || "",
        meeting_link: s.meeting_link || "",
        meeting_link_text: s.meeting_link_text || "",
        from_email: s.from_email || "",
        from_name: s.from_name || "",
        tagline: s.tagline || "",
        header_color: s.header_color || "#0C2E3C",
        accent_color: s.accent_color || "#C8E64E",
        website_url: s.website_url || "",
        footer_location: s.footer_location || "",
      });
    }
  }, [settings]);

  // Fetch current deck
  useEffect(() => {
    axios.get(`${API}/sponsor-pipeline/deck`, { withCredentials: true })
      .then((res) => setDeck(res.data?.deck_id ? res.data : null))
      .catch(() => {})
      .finally(() => setDeckLoading(false));
  }, []);

  const handleDeckUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF files"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/sponsor-pipeline/deck/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setDeck(res.data);
      toast.success("Sponsorship deck uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (deckInputRef.current) deckInputRef.current.value = ""; }
  };

  const handleDeckRemove = async () => {
    try {
      await axios.delete(`${API}/sponsor-pipeline/deck`, { withCredentials: true });
      setDeck(null);
      toast.success("Deck removed");
    } catch { toast.error("Failed to remove deck"); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/sponsor-pipeline/settings`, { ...form, org: "" }, { withCredentials: true });
      toast.success("Settings saved");
      onSaved();
    } catch { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="sponsor-settings-editor">
      <p className="text-sm text-cfc-text-muted">Configure template variables and email branding for sponsor outreach.</p>

      <p className="text-[10px] font-bold text-cfc-primary uppercase tracking-wider">Template Variables</p>
      <div>
        <label className="text-xs font-bold text-cfc-text-muted uppercase">Club Name</label>
        <Input value={form.club_name} onChange={(e) => setForm((p) => ({ ...p, club_name: e.target.value }))} className="mt-1" data-testid="setting-club-name" />
      </div>
      <div>
        <label className="text-xs font-bold text-cfc-text-muted uppercase">Rep Name</label>
        <Input value={form.rep_name} onChange={(e) => setForm((p) => ({ ...p, rep_name: e.target.value }))} placeholder="e.g. Justin" className="mt-1" data-testid="setting-rep-name" />
      </div>
      <div>
        <label className="text-xs font-bold text-cfc-text-muted uppercase">Rep Email</label>
        <Input value={form.rep_email} onChange={(e) => setForm((p) => ({ ...p, rep_email: e.target.value }))} placeholder="e.g. justin@cvilleunitedfc.com" className="mt-1" data-testid="setting-rep-email" />
      </div>
      <div>
        <label className="text-xs font-bold text-cfc-text-muted uppercase">Meeting Link URL</label>
        <Input value={form.meeting_link} onChange={(e) => setForm((p) => ({ ...p, meeting_link: e.target.value }))} placeholder="e.g. https://calendly.com/your-link" className="mt-1" data-testid="setting-meeting-link" />
      </div>
      <div>
        <label className="text-xs font-bold text-cfc-text-muted uppercase">Meeting Link Text</label>
        <Input value={form.meeting_link_text} onChange={(e) => setForm((p) => ({ ...p, meeting_link_text: e.target.value }))} placeholder="e.g. Schedule a Meeting With Us" className="mt-1" data-testid="setting-meeting-link-text" />
        <p className="text-[10px] text-cfc-text-muted mt-1">The clickable text shown in emails. If blank, the URL itself is shown.</p>
      </div>

      <Separator />
      <p className="text-[10px] font-bold text-cfc-primary uppercase tracking-wider">Email Branding</p>
      <p className="text-xs text-cfc-text-muted">Controls the visual wrapper around every outgoing sponsor email (header, footer, colors).</p>
      <div>
        <label className="text-xs font-bold text-cfc-text-muted uppercase">From Email</label>
        <Input value={form.from_email} onChange={(e) => setForm((p) => ({ ...p, from_email: e.target.value }))} placeholder="e.g. info@cvilleunitedfc.com" className="mt-1" data-testid="setting-from-email" />
      </div>
      <div>
        <label className="text-xs font-bold text-cfc-text-muted uppercase">From Name</label>
        <Input value={form.from_name} onChange={(e) => setForm((p) => ({ ...p, from_name: e.target.value }))} placeholder="e.g. CVille United FC" className="mt-1" data-testid="setting-from-name" />
      </div>
      <div>
        <label className="text-xs font-bold text-cfc-text-muted uppercase">Tagline</label>
        <Input value={form.tagline} onChange={(e) => setForm((p) => ({ ...p, tagline: e.target.value }))} placeholder="e.g. Nil Desperandum. Never Despair." className="mt-1" data-testid="setting-tagline" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-bold text-cfc-text-muted uppercase">Header Color</label>
          <div className="flex items-center gap-2 mt-1">
            <input type="color" value={form.header_color} onChange={(e) => setForm((p) => ({ ...p, header_color: e.target.value }))} className="w-8 h-8 rounded cursor-pointer border border-cfc-border" data-testid="setting-header-color" />
            <Input value={form.header_color} onChange={(e) => setForm((p) => ({ ...p, header_color: e.target.value }))} className="flex-1 font-mono text-xs" />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold text-cfc-text-muted uppercase">Accent Color</label>
          <div className="flex items-center gap-2 mt-1">
            <input type="color" value={form.accent_color} onChange={(e) => setForm((p) => ({ ...p, accent_color: e.target.value }))} className="w-8 h-8 rounded cursor-pointer border border-cfc-border" data-testid="setting-accent-color" />
            <Input value={form.accent_color} onChange={(e) => setForm((p) => ({ ...p, accent_color: e.target.value }))} className="flex-1 font-mono text-xs" />
          </div>
        </div>
      </div>
      <div>
        <label className="text-xs font-bold text-cfc-text-muted uppercase">Website URL</label>
        <Input value={form.website_url} onChange={(e) => setForm((p) => ({ ...p, website_url: e.target.value }))} placeholder="e.g. https://cvilleunitedfc.com" className="mt-1" data-testid="setting-website-url" />
      </div>
      <div>
        <label className="text-xs font-bold text-cfc-text-muted uppercase">Footer Location</label>
        <Input value={form.footer_location} onChange={(e) => setForm((p) => ({ ...p, footer_location: e.target.value }))} placeholder="e.g. Charlottesville, VA" className="mt-1" data-testid="setting-footer-location" />
      </div>

      {/* Preview */}
      <div className="rounded-sm border border-cfc-border overflow-hidden" data-testid="branding-preview">
        <div className="p-3 text-center" style={{ backgroundColor: form.header_color }}>
          <h3 className="text-sm font-bold tracking-wider" style={{ color: form.accent_color }}>{(form.club_name || "CLUB NAME").toUpperCase()}</h3>
          {form.tagline && <p className="text-[10px] mt-0.5" style={{ color: form.accent_color + "99" }}>{form.tagline}</p>}
        </div>
        <div className="p-3 bg-white text-xs text-gray-500">Email body content goes here...</div>
        <div className="p-2 text-center text-[10px]" style={{ backgroundColor: form.header_color, color: form.accent_color + "80" }}>
          {form.club_name || "Club"} | {form.footer_location || "Location"}
        </div>
      </div>

      <Separator />
      <p className="text-[10px] font-bold text-cfc-primary uppercase tracking-wider">Sponsorship Deck (PDF)</p>
      <p className="text-xs text-cfc-text-muted">Upload your sponsorship deck to attach it to bulk sponsor emails.</p>
      {deckLoading ? (
        <div className="flex items-center gap-2 text-xs text-cfc-text-muted"><Loader2 size={14} className="animate-spin" /> Loading...</div>
      ) : deck ? (
        <div className="flex items-center gap-3 p-3 bg-cfc-cream-alt border border-cfc-border rounded-sm" data-testid="deck-info">
          <FileText size={20} className="text-cfc-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-cfc-primary truncate">{deck.filename}</p>
            <p className="text-[10px] text-cfc-text-muted">{(deck.size / 1024).toFixed(0)} KB</p>
          </div>
          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={handleDeckRemove} data-testid="remove-deck-btn">
            <Trash2 size={14} />
          </Button>
        </div>
      ) : (
        <div>
          <input type="file" ref={deckInputRef} accept=".pdf" onChange={handleDeckUpload} className="hidden" />
          <Button variant="outline" className="border-cfc-primary text-cfc-primary" onClick={() => deckInputRef.current?.click()} disabled={uploading} data-testid="upload-deck-btn">
            {uploading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />}
            Upload PDF
          </Button>
        </div>
      )}

      <Button className="w-full bg-cfc-primary text-white" onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
        {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Save Settings
      </Button>
    </div>
  );
};

export default SponsorPipeline;
